@if (isActive()) {
  <section class="audit-list">
    <div class="audit-list__header">
      <div>
        <h2 class="audit-list__title">{{ i18n.t('auditList.title') }}</h2>
        <p class="audit-list__meta">{{ i18n.t('auditList.meta') }}</p>
      </div>

      <div
        class="app-view-toggle"
        role="tablist"
        [attr.aria-label]="i18n.t('auditList.viewMode.label')"
      >
        <button
          class="app-view-toggle__button"
          type="button"
          [class.app-view-toggle__button--active]="viewMode() === 'list'"
          [attr.aria-pressed]="viewMode() === 'list'"
          [attr.aria-label]="i18n.t('auditList.viewMode.list')"
          [attr.title]="i18n.t('auditList.viewMode.list')"
          (click)="setViewMode('list')"
        >
          <span class="fa-solid fa-table-list" aria-hidden="true"></span>
        </button>
        <button
          class="app-view-toggle__button"
          type="button"
          [class.app-view-toggle__button--active]="viewMode() === 'tiles'"
          [attr.aria-pressed]="viewMode() === 'tiles'"
          [attr.aria-label]="i18n.t('auditList.viewMode.tiles')"
          [attr.title]="i18n.t('auditList.viewMode.tiles')"
          (click)="setViewMode('tiles')"
        >
          <span class="fa-solid fa-table-cells-large" aria-hidden="true"></span>
        </button>
      </div>
    </div>

    @if (loadError(); as error) {
      <div class="form-message"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
    }

    @if (isLoading()) {
      <div class="app-empty-state"><span class="app-message__icon app-message__icon--loading fa-solid fa-spinner fa-spin" aria-hidden="true"></span> {{ i18n.t('auditList.states.loading') }}</div>
    } @else if (!getVisibleAudits().length) {
      <div class="app-empty-state"><span class="app-message__icon app-message__icon--info fa-solid fa-circle-info" aria-hidden="true"></span> {{ i18n.t('auditList.states.empty') }}</div>
    } @else if (viewMode() === 'list') {
      <div class="app-data-table-shell">
        <table class="app-data-table">
          <thead>
            <tr class="app-data-table__header-row">
              <th>{{ i18n.t('auditList.columns.name') }}</th>
              <th>{{ i18n.t('auditList.columns.description') }}</th>
              <th>{{ i18n.t('auditList.columns.checklists') }}</th>
              <th>{{ i18n.t('auditList.columns.status') }}</th>
              <th>{{ i18n.t('auditList.columns.updatedAt') }}</th>
              <th class="audit-list__actions-header">{{ i18n.t('auditList.columns.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            @for (audit of getVisibleAudits(); track trackByAuditId($index, audit)) {
              <tr>
                <td class="audit-list__cell-title">
                  <div class="audit-list__cell-primary">{{ audit.name }}</div>
                </td>
                <td>{{ audit.description || i18n.t('auditList.fallbacks.noDescription') }}</td>
                <td>{{ getChecklistCount(audit) }}</td>
                <td>
                  <span class="app-status-chip" [ngClass]="getStatusClass(audit)">
                    {{ getStatusLabel(audit) }}
                  </span>
                </td>
                <td>{{ audit.updated_at | date: 'yyyy-MM-dd HH:mm' }}</td>
                <td class="audit-list__actions-cell">
                  <div class="app-row-actions">
                    <button
                      class="secondary-button secondary-button--compact secondary-button--indigo app-row-action-button"
                      type="button"
                      [attr.title]="i18n.t('auditList.actions.edit')"
                      [attr.aria-label]="i18n.t('auditList.actions.edit')"
                      (click)="editAudit(audit)"
                    >
                      <span class="fa-solid fa-pen-to-square" aria-hidden="true"></span>
                    </button>
                    <button
                      class="app-button--danger primary-button primary-button--compact app-row-action-button"
                      type="button"
                      [attr.title]="i18n.t('auditList.actions.delete')"
                      [attr.aria-label]="i18n.t('auditList.actions.delete')"
                      (click)="deleteAudit(audit)"
                    >
                      <span class="fa-solid fa-trash-can" aria-hidden="true"></span>
                    </button>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    } @else {
      <div class="app-card-grid">
        @for (audit of getVisibleAudits(); track trackByAuditId($index, audit)) {
          <article class="app-data-card audit-list__card">
            <div class="app-data-card__header audit-list__card-header">
              <h3 class="audit-list__card-title">{{ audit.name }}</h3>
              <span class="app-status-chip" [ngClass]="getStatusClass(audit)">
                {{ getStatusLabel(audit) }}
              </span>
            </div>

            <p class="audit-list__card-description">
              {{ audit.description || i18n.t('auditList.fallbacks.noDescription') }}
            </p>

            <dl class="audit-list__details">
              <div>
                <dt>{{ i18n.t('auditList.columns.checklists') }}</dt>
                <dd>{{ getChecklistCount(audit) }}</dd>
              </div>
              <div>
                <dt>{{ i18n.t('auditList.columns.updatedAt') }}</dt>
                <dd>{{ audit.updated_at | date: 'yyyy-MM-dd HH:mm' }}</dd>
              </div>
            </dl>

            <footer class="app-data-card__footer audit-list__card-footer">
              <div class="app-row-actions">
                <button
                  class="secondary-button secondary-button--compact secondary-button--indigo app-row-action-button"
                  type="button"
                  [attr.title]="i18n.t('auditList.actions.edit')"
                  [attr.aria-label]="i18n.t('auditList.actions.edit')"
                  (click)="editAudit(audit)"
                >
                  <span class="fa-solid fa-pen-to-square" aria-hidden="true"></span>
                </button>
                <button
                  class="app-button--danger primary-button primary-button--compact app-row-action-button"
                  type="button"
                  [attr.title]="i18n.t('auditList.actions.delete')"
                  [attr.aria-label]="i18n.t('auditList.actions.delete')"
                  (click)="deleteAudit(audit)"
                >
                  <span class="fa-solid fa-trash-can" aria-hidden="true"></span>
                </button>
              </div>
            </footer>
          </article>
        }
      </div>
    }

    <app-confirmation-dialog
      [isOpen]="deleteDialogOpen()"
      [title]="i18n.t('auditList.deleteConfirmation.title')"
      [message]="i18n.t('auditList.deleteConfirmation.message')"
      [confirmLabel]="
        isDeleting()
          ? i18n.t('auditList.deleteConfirmation.deleting')
          : i18n.t('common.actions.yes')
      "
      [cancelLabel]="i18n.t('common.actions.cancel')"
      [confirmDisabled]="isDeleting()"
      (confirmed)="confirmAuditDeletion()"
      (cancelled)="cancelAuditDeletion()"
    />
  </section>
}
